import React from 'react';
import type { AccessibilityProps } from 'react-native';

import { HybridOnPressHandler } from '@rover/kibble/core/utils';
import type {
  FontWeightProps,
  TextAlignProps,
  TextTransformProps,
} from '@rover/kibble/styles/styled-system';
import type {
  ThemeHeadingSizes,
  ThemeSpacing,
  ThemeTextDecoration,
  ThemeTextSizes,
} from '@rover/kibble/styles/themeTypes';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import type { SpacingSystemProps, SxStyleProp } from '../styledSystemPropTypes';
/** Link visual style variants */
export type LinkVariant =
  | 'primary'
  | 'secondary'
  | 'primaryContrast'
  | 'secondaryContrast'
  | 'navigation';

/** For now, this is only props that are available on web.  Ignored on React Native. */
export type LinkWebOnlyProps = {
  /** A name for screen readers that replaces the link's visible text. */
  'aria-label'?: string;
  /**
   * The id of an element whose text names the link for screen readers, in place of its own text.
   */
  'aria-labelledby'?: string;
  /**
   * The element inside the link, such as `h2` to make it a heading too. Defaults to `span` up to
   * size `300` and `div` above.
   */
  as?: 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

/** For now, this is only props that are available on Rxn.  Ignored on Web. */
export type LinkRxnOnlyProps = Omit<AccessibilityProps, 'role'>;

/** Base props without the platform-specific nested props */
export type BaseLinkProps = SpacingSystemProps &
  FontWeightProps &
  TextAlignProps &
  TextTransformProps & {
    /** The link's text. It's required: there is no icon-only link. */
    children: NonNullable<React.ReactNode>;
    /**
     * An icon from `@rover/icons` beside the text: 16px up to size `300`, 20px at `400` and 24px at
     * `500` and `600`.
     */
    icon?: SvgIcon;
    /** Which side of the text the icon sits on. Defaults to `left`. */
    iconAlignment?: 'left' | 'right';
    /**
     * The type style. `50` to `300` match Text; `400` to `600` match Heading, semibold by default
     * and with space below. Defaults to `200`.
     */
    size?: ThemeTextSizes | ThemeHeadingSizes;
    /** Underlines the text. On web the link also underlines on hover. Defaults to `true`. */
    underline?: boolean;
    /**
     * The color. Use a `Contrast` variant on a dark background. `webProps` and `rxnProps` can't
     * change it. Defaults to `primary`.
     */
    variant?: LinkVariant;
    /** An id for finding the link in tests. */
    testID?: string;
    /**
     * Lets the link wrap with the text around it, inside a sentence. On React Native, `icon` is
     * ignored.
     */
    asInlineLink?: true;
    /**
     * Runs when the link is pressed, and Link waits for it before opening `href`. If it throws, web
     * still opens `href` and React Native doesn't.
     */
    onPress?: HybridOnPressHandler;
    /**
     * Theme-aware styles for the link's outer element. On web, use `webProps.sx`: a top-level `sx`
     * replaces Link's own styles and any `webProps.sx`.
     */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;
    /**
     * Where the link goes. On React Native the operating system opens it, and `#` opens nothing.
     */
    href?: string;
    /**
     * The link's `rel` attribute, on web only. With `onPress`, Link also passes `noopener` or
     * `noreferrer`, when set here, to the window it opens.
     */
    rel?: 'noopener' | 'noreferrer' | 'nofollow' | 'noopener noreferrer';
    /** Where to open `href`, such as `_blank` for a new tab, on web only. Defaults to `_self`. */
    target?: '_blank' | '_self' | '_parent' | '_top' | string;
  };

export type LinkCommonProps = BaseLinkProps & {
  /**
   * Props for web only, such as `aria-label` and `as`, and web overrides of other props, though
   * some, such as `size`, do nothing. The browser follows `href` without waiting for an `onPress`
   * here.
   */
  webProps?: Partial<Omit<BaseLinkProps, keyof LinkWebOnlyProps | 'variant'> & LinkWebOnlyProps>;
  /**
   * Props for React Native only, such as `accessibilityHint`, and native overrides such as
   * `onPress`. Without `asInlineLink` the role is always `link`; with it, only `accessibilityRole`
   * and `onPress` here take effect.
   */
  rxnProps?: Partial<Omit<BaseLinkProps, keyof LinkRxnOnlyProps | 'variant'> & LinkRxnOnlyProps>;
};

export type LinkVariantMap<T> = {
  [P in LinkVariant]: T;
};

export const LinkColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_base',
  primaryContrast: 'interactive_link.primary_inverse_base',
  secondary: 'interactive_link.secondary_base',
  secondaryContrast: 'interactive_link.secondary_inverse_base',
  navigation: 'interactive_text.navigation_base', // used in NavBar
};

export const LinkHoverColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_pressed',
  primaryContrast: 'interactive_link.primary_inverse_pressed',
  secondary: 'interactive_link.secondary_pressed',
  secondaryContrast: 'interactive_link.secondary_inverse_pressed',
  navigation: 'interactive_text.navigation_pressed', // used in NavBar
};

export type LinkSizeMap<T> = {
  '50': T;
  '100': T;
  '200': T;
  '300': T;
  '400': T;
  '500': T;
  '600': T;
};

// maps icon size based on text size
export const IconSizeMap: LinkSizeMap<string> = {
  '50': '16px',
  '100': '16px',
  '200': '16px',
  '300': '16px',
  '400': '20px',
  '500': '24px',
  '600': '24px',
};

// used on RxN to recreate Heading bottom margins
export const BottomMarginMap: LinkSizeMap<ThemeSpacing> = {
  '50': '0x',
  '100': '0x',
  '200': '0x',
  '300': '0x',
  '400': '5x',
  '500': '6x',
  '600': '8x',
};

export type TypographyComponentProps<StylePropName extends 'sx' | 'style'> = {
  textDecoration: ThemeTextDecoration; // controls underline

  mb?: '0x'; // used on RxN to suppress Heading margin bottoms that mess with flexbox aligning the icon
} & {
  [P in StylePropName]: {
    color: string; // text color

    textDecorationColor?: string; // used on RxN to control underline color

    display?: P extends 'sx' ? 'inline' | 'inline-flex' : undefined; // on web we use inline for props.useInlineHeadingHack, on native we don't use this
    alignItems?: 'center'; // used on web to align icon with link text
    '&:hover'?: { color: string; textDecoration: string }; // used on web for hover styles
  };
};

export const getIsTextSized = (size: ThemeTextSizes | ThemeHeadingSizes): size is ThemeTextSizes =>
  size === '50' || size === '100' || size === '200' || size === '300';
